import React from 'react'

import { Box, Stack } from '@mui/material'

import { FieldLabel, Skeleton } from '@repo/ui'
import { StickyFormHeader } from 'components'

/**
 * Loading placeholder for the internal Create/Edit company form (§19).
 *
 * ## What it replaces
 *
 * `{isLoading ? <Loader /> : …}` — a centred spinner in place of a 94px sticky header and a 448px
 * field stack.
 *
 * Only reachable in EDIT mode: the company query is `enabled: isEdit`, and a disabled react-query
 * query is not `isLoading`. So the title is "Edit company", not the create variant.
 *
 * ## Measured, not guessed
 *
 * Off `/internal/companies/:id/edit` (viewport 1711, sidebar collapsed, content x=104 w=1583):
 *
 *   outer Box       y=24  h=606   (`px/pt/pb: 24px`)
 *   sticky header   y=48  h=94    (an 8px wayfinding row + a 49px title row at y=76)
 *   header actions  Cancel 82 / Save 67, h=40
 *   body stack      y=158 h=448   (`mt: 16px`, `spacing: 12px`)
 *   logo control    y=158 h=64    — a 64px logo preview + its 115x37 Edit button
 *   field stack     y=234 w=300 h=372 — four 75px fields at 24px gaps (75x4 + 24x3)
 *
 * ## Construction
 *
 * The header is the real `StickyFormHeader` with its real title, and each field is the real
 * `FieldLabel` with its real label text — so the 18px label and the 8px label→control gap that make
 * each field 75 are right by construction. The literals are the control height (49) and the two
 * action widths, neither derivable before the fetch.
 */
export const CompanyEditPageSkeleton = () => {
  const Field = ({ label }: { label: string }) => (
    <Box>
      <FieldLabel label={label} />
      <Skeleton variant="rounded" height={49} />
    </Box>
  )

  return (
    <Box px="24px" pt="24px" pb="24px">
      <StickyFormHeader
        title="Edit company"
        actions={
          <>
            {/* Bars, not real Buttons: Save would submit an empty form. 40px is the header-action
                height `headerActionGroupSx` enforces. */}
            <Skeleton variant="rounded" width={82} height={40} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={67} height={40} sx={{ borderRadius: 2 }} />
          </>
        }
      />

      <Stack spacing="12px" mt="16px">
        {/* Logo control: the 64px preview and the Edit button beside it, at the real 24px gap. */}
        <Stack direction="row" spacing={6} alignItems="center" sx={{ height: 64 }}>
          <Skeleton variant="rounded" width={64} height={64} />
          <Skeleton variant="rounded" width={115} height={37} sx={{ borderRadius: 2 }} />
        </Stack>

        <Stack spacing="24px" width={300}>
          <Field label="Company Name" />
          <Field label="Email" />
          <Field label="Country" />
          <Field label="Price ID" />
        </Stack>
      </Stack>
    </Box>
  )
}
